import { IVfxTask } from "../../core/runtime/vfx_task_runner.js";
import { Vfx2DRuntime } from "../../vfx_2d_runtime.js";
import { Vector2 } from "../../../common/vector2.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { GenericSpriteBatch } from "../../core/batching/generic_sprite_batch.js";
import { IController, StopBehavior } from "../../core/types/controller_types.js";
import { ColorMode } from "../../core/types/webgl_types.js";

export class CoinIconEffectController implements IVfxTask, IController
{
  private target: Vector2;
  private icon_size: Vector2;
  private start_time: number;
  private stable_id: number = -1;
  private plus_stable_id: number = -1;
  private is_finished: boolean = false;
  private opacity: number = 1;
  private scale: Vector2 = new Vector2( 1, 1 );
  private fade_out_start = Number.POSITIVE_INFINITY;
  private fade_out_duration = 0;

  constructor( target: Vector2, icon_size: Vector2, start_time: number = performance.now() )
  {
    this.target = target.clone();
    this.icon_size = icon_size.clone();
    this.start_time = start_time;

    const sprite_batch = Vfx2DRuntime.onOverlayFront( GenericSpriteBatch );

    this.stable_id = sprite_batch.spawnSprite(
      ImgID.IconCoin,
      this.target,
      this.icon_size,
      { x: 1.0, y: 1.0, z: 1.0 },
      1.0,
      0.0,
      0.0, // Normal blend mode
      this.start_time,
      999999,
      ColorMode.Texture,
    );

    // The menu's green plus is a child of the coin icon. This temporary coin
    // lives on the top VFX canvas, so without a matching plus it covers the
    // real one underneath while a cross-screen coin flight is finishing.
    const plus_size = new Vector2(
      this.icon_size.x * 70 / 174,
      this.icon_size.y * 70 / 174
    );
    const plus_center = new Vector2(
      this.target.x + this.icon_size.x * 42 / 174,
      this.target.y + this.icon_size.y * 32 / 174
    );
    this.plus_stable_id = sprite_batch.spawnSprite(
      ImgID.IconPlus,
      plus_center,
      plus_size,
      { x: 1.0, y: 1.0, z: 1.0 },
      1.0,
      0.0,
      0.0,
      this.start_time,
      999999,
      ColorMode.Texture,
    );
  }

  public setPosition( pos: Vector2 ): void
  {
    this.target.copyFrom( pos );
  }

  public setOpacity( opacity: number ): void
  {
    this.opacity = opacity;
  }

  public setScale( scale: Vector2 ): void
  {
    this.scale.copyFrom( scale );
  }

  public fadeOutAt( start_time: number, duration: number ): void
  {
    this.fade_out_start = start_time;
    this.fade_out_duration = Math.max( 1, duration );
  }

  public accelerate( factor: number, now: number ): void {}

  /**
   * Явне вимикання ефекту іконки монети.
   */
  public stop( behavior?: StopBehavior, fade_duration?: number ): void
  {
    if ( this.is_finished ) return;
    this.is_finished = true;

    if ( this.stable_id !== -1 )
    {
      const sprite_batch = Vfx2DRuntime.onOverlayFront( GenericSpriteBatch );
      sprite_batch.triggerDeath( this.stable_id, performance.now() );
      this.stable_id = -1;

      if ( this.plus_stable_id !== -1 )
      {
        sprite_batch.triggerDeath( this.plus_stable_id, performance.now() );
        this.plus_stable_id = -1;
      }
    }
  }

  public onUpdate( now: number ): boolean
  {
    if ( this.is_finished ) return true;
    if ( now < this.start_time ) return false;

    if ( this.stable_id !== -1 )
    {
      const sprite_batch = Vfx2DRuntime.onOverlayFront( GenericSpriteBatch );
      const current_size = new Vector2( this.icon_size.x * this.scale.x, this.icon_size.y * this.scale.y );
      const fade_progress = Math.max(
        0,
        Math.min( 1, ( now - this.fade_out_start ) / Math.max( 1, this.fade_out_duration ) )
      );
      const rendered_opacity = this.opacity * ( 1 - fade_progress );

      sprite_batch.updateSpriteData(
        this.stable_id,
        this.target,
        current_size,
        rendered_opacity,
        0.0
      );

      if ( this.plus_stable_id !== -1 )
      {
        const plus_size = new Vector2(
          this.icon_size.x * this.scale.x * 70 / 174,
          this.icon_size.y * this.scale.y * 70 / 174
        );
        const plus_center = new Vector2(
          this.target.x + this.icon_size.x * this.scale.x * 42 / 174,
          this.target.y + this.icon_size.y * this.scale.y * 32 / 174
        );
        sprite_batch.updateSpriteData(
          this.plus_stable_id,
          plus_center,
          plus_size,
          rendered_opacity,
          0.0
        );
      }

      if ( fade_progress >= 1 )
      {
        this.stop();
        return true;
      }
    }

    return false;
  }
}
